import { getBearerToken } from '@/src/auth/token-storage';
import { normalizeApiError, type ApiErrorDetails } from './error-utils';

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:3001';

export type RawApiResult<T> =
  | { data: T; error: null }
  | { data: null; error: ApiErrorDetails };

export async function authenticatedApiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = getBearerToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(`${apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
}

function fileNameFromDisposition(contentDisposition: string | null, fallback: string): string {
  const encoded = contentDisposition
    ?.match(/filename\*=[^;]*?([^;]+)/i)?.[1]
    ?.trim()
    .replace(/^"|"$/g, '');
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  return contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] ?? fallback;
}

export async function downloadAuthenticatedFile(
  path: string,
  options: { fallbackFileName: string; signal?: AbortSignal },
): Promise<
  | { blob: Blob; fileName: string; error: null }
  | { blob: null; fileName: string; error: ApiErrorDetails }
> {
  try {
    const response = await authenticatedApiFetch(path, { signal: options.signal });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const normalizedBody = errorBody && typeof errorBody === 'object'
        ? { ...(errorBody as Record<string, unknown>), statusCode: response.status }
        : { message: response.statusText || 'Download failed.', statusCode: response.status };
      return {
        blob: null,
        fileName: options.fallbackFileName,
        error: normalizeApiError(normalizedBody, response.statusText || 'Download failed.'),
      };
    }
    return {
      blob: await response.blob(),
      fileName: fileNameFromDisposition(
        response.headers.get('Content-Disposition'),
        options.fallbackFileName,
      ),
      error: null,
    };
  } catch (error) {
    return {
      blob: null,
      fileName: options.fallbackFileName,
      error: normalizeApiError(error, 'Download failed.'),
    };
  }
}
